import { Button, cn } from '@repo/ui'
import { Check, Loader2 } from 'lucide-react'
import { useCallback, useId } from 'react'
import type { KeyboardEvent } from 'react'

export type CanvasEditState = 'editing' | 'saving' | 'saved'

const nestedEditorControlSelector =
  'input, textarea, select, button, a[href], [contenteditable="true"], [role="button"], [role="combobox"], [role="dialog"], [role="listbox"], [role="menu"], [role="textbox"]'

export function isCanvasEditShortcutTarget(target: EventTarget | null, boundary?: Element | null): boolean {
  if (!(target instanceof Element)) return false
  const owner = target.closest('[data-canvas-edit-shortcuts="true"], .ProseMirror')
  if (owner === null || (boundary != null && !boundary.contains(owner))) return false
  if (target === owner) return true
  const nestedControl = target.closest(nestedEditorControlSelector)
  return nestedControl === null || nestedControl === owner
}

export type CanvasEditActionsProps = {
  state: CanvasEditState
  fit?: { label: string; blockingReason?: string }
  error?: string
  errorId?: string
  saveDisabled?: boolean
  announceState?: boolean
  onSave: () => void
  onCancel: () => void
}

export function CanvasEditSaveAnnouncement({ saved, label }: { saved: boolean; label: string }) {
  // This region exists before its text changes. Mounting a live region already populated with
  // Saved is not announced reliably, which is why the visual saved HUD delegates to this node.
  return (
    <span role="status" aria-live="polite" aria-atomic="true" aria-label={label} className="sr-only">
      {saved ? 'Saved' : ''}
    </span>
  )
}

const stateLabel: Record<CanvasEditState, string> = {
  editing: 'Editing',
  saving: 'Saving',
  saved: 'Saved'
}

export function CanvasEditActions({
  state,
  fit,
  error,
  errorId,
  saveDisabled = false,
  announceState = true,
  onSave,
  onCancel
}: CanvasEditActionsProps) {
  const saving = state === 'saving'
  const saveShortcutId = useId()
  const cancelShortcutId = useId()

  return (
    <div className="pointer-events-none sticky bottom-3 z-20 mt-5">
      <div className="pointer-events-auto rounded-lg border bg-background p-2.5 shadow-lg">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div
            {...(announceState ? { role: 'status', 'aria-live': 'polite' as const } : {})}
            className="min-w-0 flex-1 text-xs"
          >
            <span className="inline-flex items-center gap-1.5 font-medium">
              {state === 'saving' && (
                <Loader2 className="size-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              )}
              {state === 'saved' && <Check className="size-3.5 text-primary" aria-hidden="true" />}
              {stateLabel[state]}
            </span>
            {fit !== undefined && (
              <span className="ml-2 text-muted-foreground">
                {fit.label}
                {fit.blockingReason === undefined ? '' : ` · ${fit.blockingReason}`}
              </span>
            )}
          </div>
          {state !== 'saved' && (
            <div className="flex shrink-0 items-center gap-2">
              <span id={saveShortcutId} className="sr-only">
                Save with Command or Control plus Enter.
              </span>
              <span id={cancelShortcutId} className="sr-only">
                Cancel with Escape.
              </span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                data-canvas-edit-cancel="true"
                data-canvas-edit-shortcuts="true"
                disabled={saving}
                aria-describedby={cancelShortcutId}
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                data-canvas-edit-shortcuts="true"
                disabled={saving || saveDisabled}
                aria-describedby={saveShortcutId}
                onClick={onSave}
              >
                {saving && <Loader2 className="size-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
                Save
              </Button>
            </div>
          )}
        </div>
        {error !== undefined && (
          <p id={errorId} role="alert" className={cn('mt-2 text-xs text-destructive')}>
            {error}
          </p>
        )}
      </div>
    </div>
  )
}

export function useCanvasEditShortcuts({
  disabled,
  onSave,
  onCancel
}: {
  disabled: boolean
  onSave: () => void
  onCancel: () => void
}): (event: KeyboardEvent<HTMLElement>) => void {
  return useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (disabled || event.defaultPrevented || event.nativeEvent.isComposing) return
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        onCancel()
        return
      }
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        event.stopPropagation()
        onSave()
      }
    },
    [disabled, onCancel, onSave]
  )
}
